<template>
  <div>
    <!-- ============================================================== -->
    <!-- Container fluid  -->
    <!-- ============================================================== -->
    <div class="container-fluid">
      <!-- ============================================================== -->
      <!-- Bread crumb and right sidebar toggle -->
      <!-- ============================================================== -->
      <div class="row page-titles">
        <div class="col-md-5 col-8 align-self-center">
          <ol class="breadcrumb">
            <li class="breadcrumb-item">
              <router-link to="/home">Home</router-link>
            </li>
            <li class="breadcrumb-item active">スタッフ一覧</li>
          </ol>
        </div>
      </div>
      <!-- ============================================================== -->
      <!-- End Bread crumb and right sidebar toggle -->
      <!-- ============================================================== -->
      <div class="row">
        <div class="col-12">
          <!-- Column -->
          <div class="card">
            <div class="card-body">
              <div class="dataTables_wrapper dt-bootstrap no-footer">
                <div class="row">
                  <div class="col-4">
                    <div class="p-xl-3" :class="{'has-danger': errorOfficeId ? true : false }">
                      <label class="control-label">事業所</label>
                      <select-office id="select-office" v-model="targetOfficeId"/>
                      <div class="text-danger">{{errorOfficeId}}</div>
                    </div>
                  </div>
                  <div class="col-3">
                    <div class="p-xl-3">
                      <div class="form-group">
                        <label class="control-label">スタッフ(前方一致)</label>
                        <input type="text" class="form-control" v-model="staffDisplayName" maxlength="30"/>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="row form-actions">
                  <div class="col-md-6 text-right">
                    <div class="p-xl-3">
                      <button type="submit" class="btn btn-success btn-circle" :disabled="isProcessing"
                              @click="search(1, true)">
                        <i class="fa fa-search"></i>
                      </button>
                    </div>
                  </div>
                  <div class="col-md-3 text-right">
                    <div class="p-xl-3">
                      <button v-if="isTeresaStandard" type="button" class="btn btn-block btn-secondary text-danger font-weight-bold"
                        style="margin-bottom: 8px !important;" :disabled="isProcessing"
                        @click="downloadCsvTeresaStandard">
                        テレッサ標準（スタッフ)出力
                      </button>
                      <button type="submit" class="btn btn-block btn-secondary" :disabled="isProcessing"
                              @click="downloadCsv">
                        <span class="fa fa-download"></span>ダウンロード
                      </button>
                    </div>
                  </div>
                  <div class="col-md-3 text-right">
                    <div class="data-actions">
                      <router-link to="/staff_new" class="btn btn-rounded btn-outline-info">
                        <i class="fa fa-plus"></i>スタッフを新規登録する
                      </router-link>
                    </div>
                  </div>
                </div>
                <div class="row" v-if="page">
                  <div class="col-sm-12">
                    <div class="table-responsive" v-if="page.data.length > 0">
                      <table id="myTable" class="table table-sm table-bordered dataTable">
                        <thead>
                        <tr>
                          <th :class="sortClass('login_id')" @click.prevent="sort('login_id')">ログインID</th>
                          <th :class="sortClass('display_name')" @click.prevent="sort('display_name')">スタッフ名</th>
                          <th :class="sortClass('display_name_kana')" @click.prevent="sort('display_name_kana')">フリガナ
                          </th>
                          <th>権限</th>
                          <th>登録日時</th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr v-for="staff in page.data" :key="staff.id">
                          <td>
                            {{staff.login_id}}
                          </td>
                          <td>
                            <router-link :to="`/staff/${staff.id}`">{{staff.display_name}}</router-link>
                          </td>
                          <td>{{staff.display_name_kana}}</td>
                          <td>{{staff.staff_role.display_name}}</td>
                          <td>{{staff.created_at}}</td>
                        </tr>
                        </tbody>
                      </table>
                    </div>
                    <div v-else>
                      <div class="alert alert-warning">
                        <p><i class="fa fa-info-circle p-r-10"></i>該当するデータが見つかりませんでした。</p>

                        <p>検索のヒント</p>
                        <ul>
                          <li>
                            検索条件を変更してみてください。
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
                <pagination :page="page" v-if="page && page.data.length > 0" @change="changePage"/>
              </div>
            </div>
          </div>
          <!-- Column -->
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import { useStore } from 'vuex'
import api from '../../libs/Api'
import constants from '../../constants'
import Constants from '@/constants'
import Pagination from '../../components/Pagination.vue'
import SelectOffice from '../../components/SelectOffice'
import { useSortMixin } from '../../composables/useSortMixin'
import dayjs from 'dayjs'
import { useExecutor } from '@/libs/Executor'

const store = useStore()
const { exec } = useExecutor()
const profile = computed(() => store.state.session.profile)
const isTeresaStandard = computed(() => {
  if (!profile.value?.site) return false
  return (Constants.FunctionMask.TeresaStandard & profile.value.site.function_mask) === Constants.FunctionMask.TeresaStandard
})

// Reactive state
const page = ref(null)
const targetOfficeId = ref(null)
const staffDisplayName = ref(null)
const staffDisplayNameKana = ref(null)
const isProcessing = ref(false)
const errorOfficeId = ref(null)

const { sortInfo, clearSort, sort, sortString, sortClass } = useSortMixin()

const changePage = (pageNum) => {
  search(pageNum)
}

const search = async (pageNum = 1, clearSortFlag = false) => {
  if (clearSortFlag) {
    clearSort()
  }

  exec(async () => {
    isProcessing.value = true
    const res = await api.getStaffs(
      constants.QUERY_MODE_PAGING,
      pageNum,
      targetOfficeId.value,
      staffDisplayName.value,
      staffDisplayNameKana.value,
      sortString.value
    )
    page.value = res
  }).catch((error) => {
    console.error('Error searching staff:', error)
  }).finally(() => {
    isProcessing.value = false
  })
}

const downloadCsv = async () => {
  exec(async () => {
    isProcessing.value = true
    const query = createQuery()
    const res = await api.exportStaff(query)
    saveCsvFile(res.data, 'staff')
  }).catch((error) => {
    console.error('Error downloading CSV:', error)
  }).finally(() => {
    isProcessing.value = false
  })
}

const downloadCsvTeresaStandard = async () => {
  exec(async () => {
    isProcessing.value = true
    errorOfficeId.value = null

    // validate office id
    if (!targetOfficeId.value) {
      errorOfficeId.value = '事業部は必須です。'
      return
    }

    const query = createQuery()
    const res = await api.exportTeresaStandardStaff(query)
    saveCsvFile(res.data, 'staff')
  }).catch((error) => {
    console.error('Error downloading CSV:', error)
  }).finally(() => {
    isProcessing.value = false
  })
}

const saveCsvFile = (res, filename) => {
  // CSVデータ取得 - BOM 付与
  const blob = new Blob([res], { type: 'text/csv' })

  filename += '_' + dayjs(Date.now()).format('YYYYMMDD_HHmmss') + '.csv'

  if (window.navigator.msSaveBlob) {
    // IE11 ( msSaveBlog が有効なら)
    window.navigator.msSaveBlob(blob, filename)
    window.navigator.msSaveOrOpenBlob(blob, filename)
  } else {
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.setAttribute('download', filename)
    link.click()
    window.URL.revokeObjectURL(url)
  }
}

const createQuery = () => {
  return {
    office_id: targetOfficeId.value,
    subDomain: window.location.hostname.split('.')[0]
  }
}

// Watch for sort changes
watch(() => sortInfo.sortVector, (newVal) => {
  if (!newVal) return
  search(1)
})

// Lifecycle hooks
onMounted(() => {
  // The original component had an empty mounted function
  // We keep it for consistency
})
</script>

<style scoped>
.data-actions {
  text-align: right;
}

.btn {
  margin: 0 8px !important;
}

#select-office :deep(input[type="search"]) {
  margin-top: 8px;
}

#myTable th {
    background-color: #99abb4 !important;
}

table.dataTable thead .sorting:after {
  content: "\f0dc";
  margin-left: 10px;
  font-family: "Font Awesome 5 Free" !important;
  font-weight: 900;
  cursor: pointer;
  color: rgba(50, 50, 50, 0.5);
}

#select-office :deep(svg) {
  cursor: pointer;
  margin-top: 2px;
}
</style>
